import { useState } from 'react'
import { ShoppingCart, Sparkles, Save, GitBranch } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { generateId } from '../utils/helpers'

export default function ListingWorkbench() {
  const { agents, prompts, knowledgeBases, tasks, setTasks, canExecute } = useApp()
  const listingAgents = agents.filter(a => a.module === 'Listing' && (a.status === '主用' || a.status === '测试'))
  const workflowAgents = listingAgents.filter(a => a.agentType === 'workflow')
  const singleAgents = listingAgents.filter(a => a.agentType === 'single')

  const [agentId, setAgentId] = useState('')
  const [stepId, setStepId] = useState('')
  const [productName, setProductName] = useState('')
  const [sellingPoints, setSellingPoints] = useState('')
  const [targetAudience, setTargetAudience] = useState('')
  const [keywords, setKeywords] = useState('')
  const [output, setOutput] = useState('')
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('A10')

  const agent = agents.find(a => a.id === agentId)
  const step = agent?.steps?.find(s => s.stepId === stepId)
  const prompt = prompts.find(p => p.id === (step?.systemPromptId || agent?.promptId))
  const kbs = knowledgeBases.filter(k => (step?.knowledgeIds || agent?.knowledgeIds || []).includes(k.id))

  const execute = () => {
    if (!canExecute()) return alert('无权限')
    if (!agentId) return alert('请选择Agent')
    if (agent?.agentType === 'workflow' && !stepId) return alert('请选择Step')
    if (!productName) return alert('请输入产品名称')
    setLoading(true)
    setTimeout(() => {
      const prefix = step ? `[Step ${step.stepNumber}: ${step.stepName}]\n` : ''
      setOutput(prefix + `【${activeTab} 优化结果】\n\n标题：${productName} - ${sellingPoints.split('、')[0] || 'Premium Quality'} - ${targetAudience || 'For Home Use'}\n\n五点描述：\n1. ${sellingPoints.split('、')[0] || 'High quality material'} ensures long-lasting durability...\n2. Designed specifically for ${targetAudience || 'all users'}...\n3. ${sellingPoints.split('、')[1] || 'Easy to install'} - no tools required...\n4. Compatible with ${keywords.split(',')[0] || 'standard accessories'}...\n5. Backed by our satisfaction guarantee...\n\n后台关键词：\n${keywords || 'premium, quality, durable, easy, professional'}\n\n${activeTab === 'A10' ? 'A10评分预测: 标题85 | 图片90 | 五点82 | 关键词78 | 价格75' : ''}\n${activeTab === 'Rufus' ? 'Rufus优化建议: 增加场景化描述，补充使用场景QA' : ''}\n${activeTab === 'Cosmo' ? 'Cosmo优化建议: 丰富多意图关联属性，添加常识推理内容' : ''}\n${activeTab === 'Alexa' ? 'Alexa优化建议: 增加FAQ格式内容，优化语音搜索长尾词' : ''}`)
      setLoading(false)
    }, 1500)
  }

  const save = () => {
    const newTask = { id: generateId(), name: `${productName} ${activeTab}优化`, type: 'Listing',
      workflowId: agent?.agentType === 'workflow' ? agent.id : '', stepId: stepId || '',
      agentId, promptVersion: prompt?.version || '-', knowledgeVersion: kbs.map(k => k.version).join(', ') || '-',
      model: step?.defaultModel || agent?.defaultModel || '-',
      inputFiles: [], inputText: `产品: ${productName}\n卖点: ${sellingPoints}\n受众: ${targetAudience}\n关键词: ${keywords}`,
      aiOutput: output, humanEdit: null, adopted: false, reviewStatus: '待审核',
      owner: 'Leo', reviewer: '', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    setTasks([newTask, ...tasks]); alert('已保存到任务列表')
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <ShoppingCart size={24} className="text-purple-600" />
        <h2 className="text-xl font-bold text-slate-800">Listing 优化台</h2>
      </div>

      <div className="flex gap-2 flex-wrap">
        {['A10', 'IDQ', 'Rufus', 'Cosmo', 'Alexa'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>{tab}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">选择 Agent / 工作流</label>
            <select className="select" value={agentId} onChange={e => { setAgentId(e.target.value); setStepId('') }}>
              <option value="">请选择...</option>
              {workflowAgents.length > 0 && <optgroup label="工作流">{workflowAgents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</optgroup>}
              {singleAgents.length > 0 && <optgroup label="单 Agent">{singleAgents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</optgroup>}
            </select>
          </div>

          {agent?.agentType === 'workflow' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">选择 Step</label>
              <div className="flex gap-2 flex-wrap">
                {agent.steps?.map(s => (
                  <button key={s.stepId} onClick={() => setStepId(s.stepId)}
                    className={`px-3 py-1.5 rounded-lg text-sm ${stepId === s.stepId ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    {s.stepNumber}. {s.stepName}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div><label className="block text-sm font-medium text-slate-700 mb-1">产品名称</label><input className="input" value={productName} onChange={e => setProductName(e.target.value)} placeholder="例如：TWS蓝牙耳机" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">核心卖点（用、分隔）</label><textarea className="textarea h-20" value={sellingPoints} onChange={e => setSellingPoints(e.target.value)} placeholder="降噪40dB、续航30小时、蓝牙5.3" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">目标受众</label><input className="input" value={targetAudience} onChange={e => setTargetAudience(e.target.value)} placeholder="例如：通勤族、学生、运动爱好者" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">竞品关键词（用,分隔）</label><input className="input" value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="wireless earbuds, noise cancelling" /></div>
          <button onClick={execute} disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2"><Sparkles size={16} /> {loading ? '优化中...' : `执行 ${activeTab} 优化`}</button>
        </div>

        <div className="space-y-4">
          {agent && (
            <div className="card p-4 bg-purple-50 border-purple-200">
              <div className="text-sm text-purple-800 font-medium mb-1">当前: {agent.name} {step && `- ${step.stepName}`}</div>
              <div className="text-xs text-purple-600">Prompt: {prompt?.name || '无'} | 知识库: {kbs.map(k => k.name).join(', ') || '无'}</div>
            </div>
          )}
          {output && (
            <div className="card p-5">
              <h3 className="font-semibold text-slate-800 mb-3">优化结果</h3>
              <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 whitespace-pre-wrap font-mono min-h-[200px]">{output}</div>
              <div className="flex gap-3 mt-4"><button onClick={save} className="btn-primary flex items-center gap-2"><Save size={14} /> 保存结果</button></div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
